import React from 'react';

import { Box, BoxProps, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { useCurrencyContext } from '@shared/utils/currency';
import { StyledProductCtaContainer, StyledProductTileText } from './SearchResultTile.styles';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { StarRatingWithLabel } from '../../../../../ProductDetails/components/ProductRating';
import { ProductType } from '../../../../UniversalProdutcSearch.types';

const PriceText = ({ price, ...restProps }: { price: number } & BoxProps) => {
  const currency = useCurrencyContext();
  return (
    <TextV2 typographyVariant="label2" {...restProps}>
      {currency.formatCurrency(price, { currency: 'USD', form: 'short' })}
    </TextV2>
  );
};

export type SearchResultTileProps = {
  itemPosition: number;
  data: ProductType;
  ctaText: string;
  onSearchResultClick: (itemPosition: number, searchResult: ProductType, telemetryLabel: 'grid' | 'viewGift') => void;
};

const ProductTileMetadata: React.FC<BoxProps> = ({ children, ...rest }) => {
  return (
    <Flex flexDirection="column" rowGap={1} marginTop={4} {...rest}>
      {children}
    </Flex>
  );
};

export const SearchResultTile = (props: SearchResultTileProps) => {
  const { ctaText, data, itemPosition, onSearchResultClick } = props;

  return (
    <Box
      cursor="pointer"
      __css={{
        _hover: {
          [StyledProductCtaContainer]: {
            display: 'block'
          }
        }
      }}
      role="group"
      onClick={e => {
        const telemetryLabel = (e.target as HTMLElement).dataset?.telemetryLabel;
        onSearchResultClick(itemPosition, data || null, telemetryLabel === 'viewGift' ? 'viewGift' : 'grid');
      }}
    >
      <Box
        width="100%"
        paddingTop="100%"
        backgroundSize="contain"
        backgroundRepeat="no-repeat"
        backgroundPosition="center"
        position="relative"
        style={{ backgroundImage: `url('${data?.photos[0]?.url}')` }}
      >
        <StyledProductCtaContainer display="none" position="absolute" bottom={5} width={'calc(100% - 68px)'} transform={'translateX(-50%)'} left="50%">
          <ButtonV2
            fullWidth
            shape="square"
            intent="neutral"
            variant="outline"
            backgroundColor={'mono2'}
            opacity={0.8}
            data-telemetry-label="viewGift"
            _hover={{
              backgroundColor: 'mono4'
            }}
            _groupActive={{
              backgroundColor: 'mono3'
            }}
          >
            {ctaText}
          </ButtonV2>
        </StyledProductCtaContainer>
      </Box>
      <ProductTileMetadata>
        <StyledProductTileText typographyVariant={['hed1', null, null, 'hed2']} textOverflow="ellipsis" overflow="hidden">
          {data?.title}
        </StyledProductTileText>
        <Flex columnGap={3}>
          <PriceText price={Number(data?.price?.valueInMinorUnits || data?.listPrice?.valueInMinorUnits)} color={data?.price?.valueInMinorUnits ? 'negative6' : undefined} />
          {data?.price?.valueInMinorUnits && <PriceText textDecoration="line-through" price={Number(data?.listPrice?.valueInMinorUnits)} />}
        </Flex>
        {typeof data?.rating?.averageRating === 'number' && typeof data?.rating.reviewsCount === 'number' && (
          <StarRatingWithLabel
            iconSize={14}
            labelVariant="totalReviewCount"
            rating={data?.rating.averageRating}
            showPartialRating={true}
            totalReviewCount={data?.rating.reviewsCount}
          />
        )}
        <TextV2 typographyVariant="label2" color="mono10">
          {data?.title}
        </TextV2>
      </ProductTileMetadata>
    </Box>
  );
};

SearchResultTile.Skeleton = () => {
  return (
    <Box>
      <SkeletonThumbnail width={'100%'} height={'unset'} paddingTop="100%" />
      <ProductTileMetadata marginTop={5} rowGap={3}>
        <SkeletonText rows={2} spacing={3} skeletonHeight={[pxToRem(16), null, null, pxToRem(18)]} />
        <SkeletonText rows={1} skeletonHeight={pxToRem(14)} width={9} />
        <SkeletonText rows={1} skeletonHeight={pxToRem(14)} width={pxToRem(80)} />
      </ProductTileMetadata>
    </Box>
  );
};
